import { useEffect, useState } from 'react'
import { Column } from 'react-table'
import Table from '../../../global/components/table/Table'
import NoFilter from '../../../global/components/table/helpers/filters/NoFilter'
import TableTopper from '../../../global/components/table/helpers/table-topper/TableTopper'
import TableTitle from '../../../global/components/table/helpers/table-title/TableTitle'
import { fetchData } from '../../../global/utils/fetch'
import { formatDate } from '../../../global/utils/date/mm-dd-yyyy'
import {
  CLEANLINESS_RANK_BY,
  CLEANLINESS_SCORE_BANDS,
  CLEANLINESS_WINDOW_DAYS,
} from '../../../global/constants/cleanliness'
import {
  ICleanlinessRankingResponse,
  ICleanlinessRankingRow,
} from '../../../../../app/types/cleanliness-score'
import { ILocationFilters } from '../../main/AuditsMain'
import { getCleanlinessRankings } from '../api'
import { CLEANLINESS_BAND_LABELS } from '../../utils/cleanliness-score-band'
import CleanlinessScoreChip from '../common/cleanliness-score/CleanlinessScoreChip'
import CleanlinessDetailModal from '../cleanliness-modal/CleanlinessDetailModal'
import styles from './CleanlinessTable.module.css'

// The tab ranks vans. Per-technician numbers go out through the Audit Score
// Report and the Van Cleanliness Score Report instead (Ops decision, 10/9).
const RANK_BY = CLEANLINESS_RANK_BY.van

interface ISelectedVan {
  objectId: string
  name: string
}

interface ICleanlinessTableProps {
  locationFilters: ILocationFilters
}

function CleanlinessTable({ locationFilters }: ICleanlinessTableProps) {
  const [rows, setRows] = useState<ICleanlinessRankingRow[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [loadFailed, setLoadFailed] = useState(false)
  const [selectedVan, setSelectedVan] = useState<ISelectedVan | null>(null)

  const locationObjectId = locationFilters.locationFilter

  useEffect(() => {
    const abortController = new AbortController()
    setIsLoading(true)
    setLoadFailed(false)

    fetchData<ICleanlinessRankingResponse>(
      getCleanlinessRankings(
        RANK_BY,
        CLEANLINESS_WINDOW_DAYS,
        locationObjectId,
      ),
      abortController.signal,
    )
      .then((response) => {
        setRows(response?.items ?? [])
      })
      .catch((error) => {
        if (abortController.signal.aborted) return
        console.error('Failed to load cleanliness rankings:', error)
        setRows([])
        setLoadFailed(true)
      })
      .finally(() => {
        if (!abortController.signal.aborted) setIsLoading(false)
      })

    return () => abortController.abort()
  }, [locationObjectId])

  // The technician(s) who drove the van inside the window. Names only, so a
  // van called "41" never sits next to a score.
  function describeTechnicians(row: ICleanlinessRankingRow): JSX.Element {
    const names = row.relatedNames
    if (names.length === 0) return <>-</>
    if (names.length === 1) return <>{names[0]}</>
    return (
      <>
        <span className={styles.multi}>{`${names.length} technicians: `}</span>
        {names.join(', ')}
      </>
    )
  }

  const columns: Column<ICleanlinessRankingRow>[] = [
    {
      Header: 'Rank',
      id: 'rank',
      accessor: (_row, index) => index + 1,
      Filter: NoFilter(),
    },
    {
      Header: 'Van',
      accessor: 'subjectName',
      Filter: NoFilter(),
    },
    {
      Header: 'Technician',
      id: 'relatedNames',
      accessor: (row) => row.relatedNames.join(', '),
      Filter: NoFilter(),
      Cell: ({
        row: { original },
      }: {
        row: { original: ICleanlinessRankingRow }
      }) => describeTechnicians(original),
    },
    {
      Header: 'Location',
      id: 'locationName',
      accessor: (row) => row.locationName ?? '-',
      Filter: NoFilter(),
    },
    {
      Header: `Score, last ${CLEANLINESS_WINDOW_DAYS} days`,
      accessor: 'rollingScore',
      Filter: NoFilter(),
      Cell: ({
        row: { original },
      }: {
        row: { original: ICleanlinessRankingRow }
      }) => <CleanlinessScoreChip score={original.rollingScore} />,
    },
    {
      Header: 'DVIRs scored',
      accessor: 'inspectionsScored',
      Filter: NoFilter(),
      Cell: ({
        row: { original },
      }: {
        row: { original: ICleanlinessRankingRow }
      }) => (
        <>{`${original.inspectionsScored} of ${original.inspectionsInWindow}`}</>
      ),
    },
    {
      Header: 'Last DVIR',
      accessor: 'lastInspectionDate',
      Filter: NoFilter(),
      Cell: ({
        row: { original },
      }: {
        row: { original: ICleanlinessRankingRow }
      }) => (
        <>
          {original.lastInspectionDate
            ? formatDate(new Date(original.lastInspectionDate))
            : '-'}
        </>
      ),
    },
  ]

  // The color key only means something once there are scores to color.
  const hasRows = rows.length > 0

  const noDataMessage = loadFailed
    ? 'Cleanliness scores are not available right now.'
    : 'No cleanliness scores yet. Rankings appear once DVIR interior photos have been scored.'

  return (
    <div className={styles.wrapper}>
      <TableTopper>
        <div className={styles.topperContent}>
          <div className={styles.titleBlock}>
            <TableTitle>Van cleanliness rankings</TableTitle>
            <span className={styles.subtitle}>
              {`DVIR interior photos, last ${CLEANLINESS_WINDOW_DAYS} days. Click a row for the inspections behind the score.`}
            </span>
          </div>
          {hasRows && (
            <div className={styles.legend}>
              <span>
                <CleanlinessScoreChip
                  score={CLEANLINESS_SCORE_BANDS.clean}
                  label={`${CLEANLINESS_SCORE_BANDS.clean}+`}
                />
                {CLEANLINESS_BAND_LABELS.clean}
              </span>
              <span>
                <CleanlinessScoreChip
                  score={CLEANLINESS_SCORE_BANDS.watch}
                  label={`${CLEANLINESS_SCORE_BANDS.watch} to ${CLEANLINESS_SCORE_BANDS.clean - 1}`}
                />
                {CLEANLINESS_BAND_LABELS.watch}
              </span>
              <span>
                <CleanlinessScoreChip
                  score={CLEANLINESS_SCORE_BANDS.watch - 1}
                  label={`Below ${CLEANLINESS_SCORE_BANDS.watch}`}
                />
                {CLEANLINESS_BAND_LABELS.poor}
              </span>
            </div>
          )}
        </div>
      </TableTopper>
      <Table
        columns={columns}
        data={rows}
        isLoading={isLoading}
        noDataMessage={noDataMessage}
        onRowClick={(row: { original: ICleanlinessRankingRow }) =>
          setSelectedVan({
            objectId: row.original.subjectObjectId,
            name: row.original.subjectName,
          })
        }
        disableSortBy
      />
      <CleanlinessDetailModal
        isOpen={!!selectedVan}
        onClose={() => setSelectedVan(null)}
        rankBy={RANK_BY}
        subjectObjectId={selectedVan?.objectId ?? null}
        subjectName={selectedVan?.name ?? ''}
      />
    </div>
  )
}

export default CleanlinessTable
